import { EventManager, Patch } from "@ploy-lib/calculation";
import { initialSender } from "@ploy-lib/snippet-messaging-types";

export const bootstrapEventProviderForSnippetMessaging = (
	eventProvider: EventManager<string, string | number | boolean>,
	allowedOrigin: string
) => {
	type DefinedPatch = Patch<string, string | number | boolean>;

	const unsubscribe = eventProvider.calculatorReady.subscribe(() => {
		//console.log("ready");

		unsubscribe();

		const messagingEvents = initialSender(allowedOrigin);

		messagingEvents.data_payload.subscribe(async data => {
			//console.log("received data payload");

			const makeNamespacePatcher = (namespace: string) => ([name, value]: [
				string,
				any
			]): DefinedPatch => ({
				overwrite: true,
				isResolve: true,
				namespace,
				target: name,
				clickTrigger:
					name === "CustomerNumber" || name === "CustomerNumberPrivate",
				value
			});

			let patches: DefinedPatch[] = [];

			if (data.Main) {
				patches.push(
					...Object.entries(data.Main)
						.filter(([_, value]) => value != null)
						.map(makeNamespacePatcher("Main"))
				);
			}

			if (data.Object) {
				patches.push(
					...Object.entries(data.Object)
						.filter(([_, value]) => value != null)
						.map(makeNamespacePatcher("Object"))
				);
			}

			if (patches.length > 0) {
				eventProvider.patch.notify({ patches });
				patches = [];
			}

			const handledCustomerFields = [
				"CustomerType",
				"CustomerNumber",
				"CustomerNumberPrivate"
			];

			if (data.Customer) {
				// Handle (main) customer by:
				//  1. Set relevant customer type
				//  2. Perform customer lookup and wait until completion
				//  3. Process residual fields

				const makeCustomerPatch = makeNamespacePatcher("Customer");

				const cust = data.Customer;

				// How to reliably detect this patch has been processed and/or triggered a service?
				// We know this patch triggers the calculator service, but what about requests in flight?
				eventProvider.patch.notify({
					patches: [makeCustomerPatch(["CustomerType", cust.CustomerType])]
				});

				let customerNumberData: [string, any] | null = null;
				if (cust.CustomerType === 1 && cust.CustomerNumberPrivate != null)
					customerNumberData = [
						"CustomerNumberPrivate",
						cust.CustomerNumberPrivate
					];
				else if (
					(cust.CustomerType === 2 || cust.CustomerType === 3) &&
					cust.CustomerNumber != null
				)
					customerNumberData = ["CustomerNumber", cust.CustomerNumber];

				if (customerNumberData != null) {
					// TODO: handle "failures" somehow?
					const customerLookupSuccess = serviceSuccessWaiter(eventProvider, [
						{ namespace: "Customer", name: "Wait" },
						{ namespace: "Customer", name: "Update" }
					]);

					// Trigger the customer lookup
					eventProvider.patch.notify({
						patches: [makeCustomerPatch(customerNumberData)]
					});

					await customerLookupSuccess;
				}

				// Processs rest of the fields
				patches.push(
					...Object.entries(cust)
						.filter(([_, value]) => value != null)
						.filter(([key, _]) => !handledCustomerFields.includes(key))
						.map(makeCustomerPatch)
				);
			}

			if (data.CoSigner) {
				patches.push(
					...Object.entries(data.CoSigner)
						.filter(([_, value]) => value != null)
						.map(makeNamespacePatcher("CoSigner"))
				);
			}

			if (data.Supplier) {
				patches.push(
					...Object.entries(data.Supplier)
						.filter(([_, value]) => value != null)
						.map(makeNamespacePatcher("Supplier"))
				);
			}


			if (data.Calculator) {
				if (patches.length > 0) {
					eventProvider.patch.notify({ patches });
					patches = [];
				}
				//Patch ObjectPrice First
				const handledPriceFields = [
					"ObjectPrice",
					"ObjectPriceIncVat",
					"ObjectPriceExVat"
				];
				patches.push(
					...Object.entries(data.Calculator)
						.filter(([_, value]) => value != null)
						.filter(([name, _]) => handledPriceFields.includes(name))
						.map(makeNamespacePatcher("Calculator"))
				);

				if (patches.length > 0) {
					eventProvider.patch.notify({ patches });
					patches = [];

					//Wait for GetConditions Success after ObjectPrice change before we send more Calculator data
					await serviceSuccessWaiter(eventProvider, [
						{ namespace: "Calculator", name: "Conditions" }
					]);
				}

				patches.push(
					...Object.entries(data.Calculator)
						.filter(([_, value]) => value != null)
						.map(makeNamespacePatcher("Calculator"))
				);
			}

			//console.log("prepared patches", patches);

			if (patches.length > 0) {
				eventProvider.patch.notify({ patches });
			}
		});
	});
};

const serviceSuccessWaiter = <TN extends string, TD>(
	eventProvider: EventManager<TN, TD>,
	orderedServicesToWaitFor: Readonly<
		Readonly<{ namespace: string; name: string }>[]
	>
) =>
	new Promise<void>((resolve, reject) => {
		let svcQueue = [...orderedServicesToWaitFor];

		const unsubscribe = eventProvider.serviceSuccess.subscribe(
			({ namespace, service }) => {
				if (
					svcQueue.length > 0 &&
					svcQueue[0].namespace === namespace &&
					svcQueue[0].name === service.name
				) {
					svcQueue = svcQueue.slice(1);
				}

				if (svcQueue.length === 0) {
					unsubscribe();
					resolve();
				}
			}
		);
	});
